import { useState, useRef, useContext, useEffect } from "react";
import { validateEmail } from "../helpers/authentication";
import styled from "styled-components";
import { passwordErrorText, SESSION_STORAGE } from "../helpers/constants";
import { useNavigate } from "react-router-dom";
import { notification } from "antd";
import { InputHeader, Spacer } from "../styles/CommonElements";
import { ThirdPartyAuth } from "../components/login";
import Button from "../components/Button";
import RevealPassword from "../components/RevealPassword";
import useLoginMutation from "../lib/useLoginMutation";
import { Link } from "react-router-dom";
import {
  DIALOG_NAME,
  useOpenParamDialog,
} from "../helpers/useDialogSearchParam";
import { APP_PRIVACY_ROUTE, APP_TERMS_ROUTE } from "../helpers/routeNames";
import { AuthContext } from "../context/AuthProvider";
import { getPostLoginHomePath } from "../helpers/accountProduct";
import Logo from "../components/general/Logo";

export default function Login() {
  const { user, userInfo } = useContext(AuthContext);
  const navigate = useNavigate();
  const openSignup = useOpenParamDialog(DIALOG_NAME.signup);
  const passwordRef = useRef();
  const [api, contextHolder] = notification.useNotification();
  const { login, loginPending, loginError } = useLoginMutation({ api });

  const [emailAddress, setEmailAddress] = useState("");
  const [password, setPassword] = useState("");
  const [revealPassword, setRevealPassword] = useState(false);

  const onEmailChange = (event) => {
    setEmailAddress(event.target.value);
  };

  const onPasswordChange = (event) => {
    setPassword(event.target.value);
  };

  useEffect(() => {
    if (user) {
      navigate(getPostLoginHomePath(userInfo), { replace: true });
    }
  }, [user, userInfo, navigate]);

  const onSubmit = async (event) => {
    event.preventDefault();
    login(
      {
        email: emailAddress,
        password,
      },
      {
        onSuccess: (userData) => {
          // The case where users haven't accepted is covered by the mutation config's onSuccess
          if (userData) {
            // check sessionstorage for redirecturl
            const redirectUrl = sessionStorage.getItem(
              SESSION_STORAGE.redirectUrl
            );
            if (redirectUrl) {
              sessionStorage.removeItem(SESSION_STORAGE.redirectUrl);
              navigate(redirectUrl, { replace: true });
              return;
            }

            navigate(getPostLoginHomePath(userData), { replace: true });
          }
        },
      }
    );
  };

  return (
    <>
      {contextHolder}
      <Container>
        <LogoHeader>
          <Logo
            showTagline={false}
            size="large"
            displayMode="icon"
            hideIconFrame
            fullColorMark
          />
        </LogoHeader>
        <Spacer $marginTop="0.5rem" />
        <AuthEyebrow>Secure channel</AuthEyebrow>
        <AuthTitle>Login</AuthTitle>
        <Spacer $marginTop="1rem" />

        <form onSubmit={onSubmit} style={{ position: "relative", zIndex: 1 }}>
          <LoginLabel>Email</LoginLabel>
          <InputContainer>
            <LoginField
              placeholder="you@email.com"
              value={emailAddress}
              onChange={onEmailChange}
              autoComplete="email"
            />
          </InputContainer>

          <Spacer $marginTop="1rem" />

          <LoginLabel>Password</LoginLabel>
          <PasswordInputContainer>
            <LoginField
              placeholder="Enter password"
              maxLength={40}
              type={revealPassword ? "text" : "password"}
              onChange={onPasswordChange}
              ref={passwordRef}
              autoComplete="current-password"
            />
            {loginError === passwordErrorText ? (
              <ErrorMark src="/icons/x-thick.svg" alt="" />
            ) : (
              <RevealPassword
                isRevealed={revealPassword}
                toggleRevealed={() => setRevealPassword(!revealPassword)}
                passwordRef={passwordRef}
              />
            )}
          </PasswordInputContainer>

          <Spacer $marginTop="0.5rem" />

          <ForgotPasswordSection>
            <AuthInlineLink
              role="button"
              tabIndex={0}
              onClick={() =>
                navigate(
                  `/forgot-password${
                    emailAddress !== "" ? `?email=${emailAddress}` : ""
                  }`
                )
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(
                    `/forgot-password${
                      emailAddress !== "" ? `?email=${emailAddress}` : ""
                    }`
                  );
                }
              }}
            >
              Forgot password?
            </AuthInlineLink>
          </ForgotPasswordSection>

          {loginError !== "" && <LoginError>{loginError}</LoginError>}

          <SignInButtons>
            <ButtonSection>
              <LuxuryLoginButton
                type="submit"
                size="large"
                disabled={
                  !(
                    validateEmail(emailAddress) &&
                    emailAddress !== "" &&
                    password !== ""
                  ) || loginPending
                }
              >
                {loginPending ? (
                  <Spinner alt="" src="/icons/spinner.svg" />
                ) : (
                  "Log In"
                )}
              </LuxuryLoginButton>
            </ButtonSection>
          </SignInButtons>
        </form>

        {/*
        <Spacer $marginTop="0.5rem" />
        <ThirdPartyAuth cta="log in" />
        */}

        <Spacer $marginTop="1rem" />

        <Terms>
          By continuing, you agree to our{" "}
          <TermsRouterLink to={APP_TERMS_ROUTE} target="_blank">
            Terms of Service
          </TermsRouterLink>
          {" and "}
          <TermsRouterLink to={APP_PRIVACY_ROUTE} target="_blank">
            Privacy Policy
          </TermsRouterLink>
          .
        </Terms>

        <Spacer $marginTop="1rem" />

        <SignUpCta>
          Don&apos;t have an account?{" "}
          <AuthInlineLink
            role="button"
            tabIndex={0}
            onClick={() => {
              openSignup();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openSignup();
              }
            }}
          >
            Sign up today.
          </AuthInlineLink>
        </SignUpCta>
      </Container>
    </>
  );
}

const Container = styled.div`
  text-align: left;
  margin: 0;
  width: 100%;
  max-width: 100%;
  position: relative;
  min-height: 100dvh;
  box-sizing: border-box;
  padding: 3rem clamp(1rem, 4vw, 1.5rem) 4rem;
  background: #020617;
  color: rgba(248, 250, 252, 0.92);
`;

const AuthEyebrow = styled.p`
  margin: 0;
  text-align: center;
  font-family: var(--font-mono, "IBM Plex Mono", ui-monospace, monospace);
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255, 87, 34, 0.95);
`;

const AuthTitle = styled.h1`
  margin: 0;
  text-align: center;
  font-family: var(--font-mono, "IBM Plex Mono", ui-monospace, monospace);
  font-size: clamp(0.72rem, 2.2vw, 0.95rem);
  font-weight: 800;
  letter-spacing: 0.08em;
  line-height: 1.35;
  color: #ffffff;
`;

const LoginLabel = styled(InputHeader)`
  color: rgba(226, 232, 240, 0.88) !important;
`;

const LoginField = styled.input`
  width: 100%;
  font-size: 1.05rem;
  padding: 0.95rem 1.1rem;
  border-radius: 0;
  border: 2px solid #1e293b;
  background: rgba(255, 255, 255, 0.04);
  color: #f8fafc;
  font-family: var(--font-secondary, "Inter", system-ui, sans-serif);
  margin-bottom: 0.5rem;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    outline: none;
    border-color: #ff5722;
    box-shadow: 0 0 0 1px #ff5722, 0 0 14px rgba(255, 87, 34, 0.25);
  }

  &::placeholder {
    color: rgba(148, 163, 184, 0.85);
  }
`;

const ButtonSection = styled.div`
  width: 100%;
  margin-top: 10px;
`;

const ErrorMark = styled.img`
  position: absolute;
  right: 14px;
  top: 35%;
  height: 14px;
  width: 14px;
`;

const LoginError = styled.h4`
  font-size: 14px;
  font-weight: 400;
  line-height: 22.4px;
  letter-spacing: 0.2px;
  text-align: left;
  margin-top: 12px;
  color: var(--color-error);
`;

const InputContainer = styled.div`
  position: relative;
`;

const SignInButtons = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 0;
  text-align: left;
  margin: auto;
  text-align: center;
  margin-top: 28px;
`;

const ForgotPasswordSection = styled.div`
  text-align: right;
  width: 100%;
  font-size: 1rem;
  font-weight: 700;
  margin-top: 12px;
  margin-bottom: 0.5rem;
  line-height: 160%;
`;

/** Same as desktop `LoginModal`: brand orange links on dark background. */
const AuthInlineLink = styled.span`
  display: inline-block;
  color: #ff5722 !important;
  cursor: pointer;
  font-family: var(--font-secondary);
  font-weight: 700;
  font-size: 0.75rem !important;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.15s ease;

  &:hover {
    color: #fb923c !important;
  }
`;

const TermsRouterLink = styled(Link)`
  color: #ff5722 !important;
  font-family: var(--font-secondary);
  font-weight: 700;
  font-size: 0.75rem !important;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.15s ease;

  &:hover {
    color: #fb923c !important;
  }
`;
const PasswordInputContainer = styled.div`
  position: relative;
`;

const SignUpCta = styled.div`
  color: #cbd5e1;
  text-align: center;
  font-family: var(--font-secondary);
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 125%;
`;

const Spinner = styled.img`
  width: 25px;
  height: 25px;
  animation: rotation 2s infinite linear;
`;

const Terms = styled.p`
  color: #cbd5e1;
  text-align: center;
  font-size: 0.75rem !important;
  font-weight: 400;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
  line-height: 1.5;
`;

const LogoHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 56px;
`;

const LuxuryLoginButton = styled(Button)`
  background: #ff5722 !important;
  color: #ffffff !important;
  font-weight: 800;
  font-size: 1.05rem;
  border-radius: 0;
  box-shadow: 3px 3px 0 #000000;
  border: 2px solid #000000 !important;
  width: 100%;
  margin-top: 0.5rem;
  font-family: var(--font-secondary);
  letter-spacing: 0.02em;
  transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.15s ease;

  &:hover:not(:disabled) {
    background: #f44e1b !important;
    color: #ffffff !important;
    transform: translate(1px, 1px);
    box-shadow: 2px 2px 0 #000000;
  }

  &:disabled {
    background: #334155 !important;
    color: #94a3b8 !important;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;
